import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Dialog,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import CountContext from '../../context/countContext';
import useVoucher from '../../hooks/useVoucher';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

function statusLabel(status) {
  const s = String(status || '').trim();
  if (s === 'pending') return 'offen';
  if (s === 'approved') return 'freigegeben';
  if (s === 'rejected') return 'abgelehnt';
  // Only parked complaint answers reach this state: the case was settled some
  // other way while the request was waiting.
  if (s === 'obsolete') return 'hinfällig';
  return s || '-';
}

function statusChip(status) {
  const s = String(status || '');
  if (s === 'pending') return <Chip size='small' label={statusLabel(s)} color='warning' />;
  if (s === 'approved') return <Chip size='small' label={statusLabel(s)} color='success' />;
  if (s === 'rejected' || s === 'obsolete')
    return <Chip size='small' label={statusLabel(s)} color='default' />;
  return <Chip size='small' label={statusLabel(s)} />;
}

// Rows of the feedback kind are decided through the feedback endpoints.
function isFeedbackApproval(requestDoc) {
  return requestDoc?.kind === 'feedbackVoucher';
}

// Who has to clear a parked answer, named the way the feedback view names them.
function describeApprovalReasons(reasons) {
  const list = Array.isArray(reasons) && reasons.length > 0 ? reasons : ['budget'];
  return list
    .map(reason => (reason === 'limit' ? 'über dem Gutschein-Limit' : 'über dem Monatsbudget'))
    .join(' und ');
}

// How a row describes the case it belongs to. A gifted balance request names its
// voucher product; a parked complaint answer names the customer it is waiting to
// reach, because that is what an approver decides about.
function subjectText(requestDoc) {
  if (!isFeedbackApproval(requestDoc)) return requestDoc.relatedVoucherProductId?.name || '-';
  const name = requestDoc.feedback?.customerFirstName?.trim();
  const booking = requestDoc.feedback?.bookingNumber?.trim();
  return [name ? `Beschwerde ${name}` : 'Beschwerde', booking ? `(${booking})` : '']
    .filter(Boolean)
    .join(' ');
}

// The comment column: an approver needs the reason, not an empty cell.
function commentText(requestDoc) {
  if (!isFeedbackApproval(requestDoc)) {
    return requestDoc.requestComment || requestDoc.reason || '-';
  }
  const parts = [`Kulanzgutschein ${describeApprovalReasons(requestDoc.reasons)}`];
  if (requestDoc.feedback?.classification) parts.push(requestDoc.feedback.classification);
  if (requestDoc.decisionComment) parts.push(`Entscheidung: ${requestDoc.decisionComment}`);
  return parts.join(' · ');
}

function formatUser(user) {
  if (!user) return '-';
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : user.username || '-';
}

function formatEuro(value) {
  return Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// The request an approver mail deep-linked to, taken from `?requestId=…&status=…`.
function readLinkedRequest() {
  if (typeof window === 'undefined') return { requestId: '', status: '' };
  const params = new URLSearchParams(window.location.search);
  return {
    requestId: (params.get('requestId') || '').trim(),
    status: (params.get('status') || '').trim().toLowerCase(),
  };
}

// The status filter a freshly opened view starts on. The approver mail links to a
// single request and asks for every status, because the default "offen" hides a
// request someone else has already decided — which made the mail look broken.
function initialStatusFilter(linked) {
  if (linked.status === 'all') return '';
  if (linked.status === 'pending' || linked.status === 'approved' || linked.status === 'rejected') {
    return linked.status;
  }
  return linked.requestId ? '' : 'pending';
}

// Sentence naming who decided a request and when — the answer an approver needs
// when the mail led here and the request is no longer open.
function linkedDecisionText(requestDoc) {
  if (!requestDoc || requestDoc.status === 'pending') return '';
  const decidedBy = formatUser(
    requestDoc.status === 'approved' ? requestDoc.meta?.approvedBy : requestDoc.meta?.rejectedBy
  );
  const decidedAtRaw =
    requestDoc.status === 'approved' ? requestDoc.meta?.approvedAt : requestDoc.meta?.rejectedAt;
  const decidedAt = decidedAtRaw ? dayjs(decidedAtRaw).format('DD.MM.YYYY HH:mm') : '';
  const verb = requestDoc.status === 'approved' ? 'freigegeben' : 'abgelehnt';
  const when = decidedAt ? ` am ${decidedAt}` : '';
  const who = decidedBy && decidedBy !== '-' ? ` von ${decidedBy}` : '';
  return `Diese Anfrage wurde${when}${who} bereits ${verb}.`;
}

function monthlyBudgetErrorMessage(error) {
  const budgetData = error?.data?.data || error?.data || {};
  return `Monatskontingent überschritten: verfügbar ${formatEuro(
    budgetData.remaining
  )} €, angefragt ${formatEuro(budgetData.requested)} €.`;
}

function GiftedBalanceVoucherApprovals() {
  const authCtx = useContext(AuthContext);
  const countCtx = useContext(CountContext);
  const {
    getGiftedBalanceVoucherRequests,
    approveGiftedBalanceVoucherRequest,
    rejectGiftedBalanceVoucherRequest,
    getVoucherDownloadURL,
  } = useVoucher();

  const [linked] = useState(readLinkedRequest);
  const [status, setStatus] = useState(() => initialStatusFilter(linked));
  const [isLoading, setIsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [busyRequestIds, setBusyRequestIds] = useState(() => new Set());
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectDialogRequest, setRejectDialogRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // Vouchers created by the last successful approval, shown in a dialog with
  // direct download links so the approver can hand them out immediately.
  const [approvedVouchers, setApprovedVouchers] = useState(null);
  // The parked complaint answer an approver is reading before deciding it.
  const [detailRequest, setDetailRequest] = useState(null);

  const busySet = useMemo(() => busyRequestIds, [busyRequestIds]);

  const markBusy = (id, next) => {
    setBusyRequestIds(prev => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await getGiftedBalanceVoucherRequests(status);
      setRequests(Array.isArray(res?.results) ? res.results : []);
    } catch (error) {
      toast.error('Fehler beim Laden der Freigabeanfragen');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  // A mail about one approval deep-links to exactly that request. Bring its row
  // into view once the list has arrived instead of leaving it to be hunted for.
  useEffect(() => {
    if (!linked.requestId || isLoading) return;
    document
      .getElementById(`gifted-request-${linked.requestId}`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [linked.requestId, isLoading, requests]);

  const handleApprove = async requestDoc => {
    const requestId = requestDoc._id;
    const isFeedback = isFeedbackApproval(requestDoc);
    markBusy(requestId, true);
    try {
      const res = await approveGiftedBalanceVoucherRequest(
        requestId,
        requestDoc.kind || 'giftedBalance'
      );

      // Approving a parked answer dispatches it: the voucher goes to the
      // customer with the mail, so there is nothing here to hand out.
      if (isFeedback) {
        setDetailRequest(null);
        toast.success('Freigegeben – die Antwort wurde an den Kunden gesendet');
      } else {
        const createdCount =
          Number(res?.count || (Array.isArray(res?.vouchers) ? res.vouchers.length : 0)) || 0;
        toast.success(
          createdCount > 1
            ? `Freigegeben: ${createdCount} Gutscheine erstellt`
            : `Freigegeben: ${res?.voucherCode || 'Gutschein erstellt'}`
        );
        const vouchers =
          Array.isArray(res?.vouchers) && res.vouchers.length > 0
            ? res.vouchers
            : res?.voucherId
              ? [res]
              : [];
        if (vouchers.length > 0) {
          setApprovedVouchers(vouchers);
        }
      }

      await load();
      countCtx?.refreshCount?.('giftedBalanceVoucherApprovals');
      countCtx?.refreshCount?.('customerExperienceFeedback');
    } catch (error) {
      if (error?.code === 'GIFTED_VOUCHER_MONTHLY_BUDGET_EXCEEDED') {
        toast.error(monthlyBudgetErrorMessage(error));
      } else if (error?.code === 'RESPONSE_ALREADY_SENT') {
        toast.error('Der Kunde hat inzwischen anderweitig eine Antwort erhalten.');
      } else if (error?.code === 'FEEDBACK_APPROVAL_NOT_PENDING') {
        toast.error('Diese Anfrage wurde bereits entschieden.');
      } else {
        toast.error('Fehler beim Freigeben der Anfrage');
      }
    } finally {
      markBusy(requestId, false);
    }
  };

  const openRejectDialog = requestDoc => {
    setRejectDialogRequest(requestDoc);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectDialogRequest(null);
    setRejectReason('');
  };

  const submitReject = async () => {
    const requestId = rejectDialogRequest?._id;
    if (!requestId) return;

    const isFeedback = isFeedbackApproval(rejectDialogRequest);
    markBusy(requestId, true);
    try {
      await rejectGiftedBalanceVoucherRequest(
        requestId,
        rejectReason.trim(),
        rejectDialogRequest.kind || 'giftedBalance'
      );
      toast.success(
        isFeedback ? 'Abgelehnt – der Fall geht mit deiner Begründung zurück' : 'Anfrage abgelehnt'
      );
      closeRejectDialog();
      setDetailRequest(null);
      await load();
      countCtx?.refreshCount?.('giftedBalanceVoucherApprovals');
      countCtx?.refreshCount?.('customerExperienceFeedback');
    } catch (error) {
      toast.error('Fehler beim Ablehnen der Anfrage');
    } finally {
      markBusy(requestId, false);
    }
  };

  // Fetch a fresh pre-signed URL for the voucher and open it with the given
  // content disposition (attachment = download, inline = view).
  const openVoucherPdfWithDisposition = async (voucherId, disposition) => {
    const response = await getVoucherDownloadURL(voucherId);
    if (!response?.preSignedURL) return null;
    const url = response.preSignedURL;
    return `${url}${url.includes('?') ? '&' : '?'}disposition=${disposition}`;
  };

  const handleDownloadVoucherPdf = async voucherId => {
    try {
      const downloadUrl = await openVoucherPdfWithDisposition(voucherId, 'attachment');
      if (!downloadUrl) return;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Fehler beim Herunterladen des Gutscheins');
    }
  };

  const handleViewVoucherPdf = async voucherId => {
    try {
      const inlineUrl = await openVoucherPdfWithDisposition(voucherId, 'inline');
      if (!inlineUrl) return;
      window.open(inlineUrl, '_blank', 'noopener,width=900,height=1100');
    } catch (error) {
      toast.error('Fehler beim Laden des Gutscheins');
    }
  };

  // Vouchers created for an approval request (populated by the backend on
  // approved requests). Supports both the batch and the legacy single field.
  const getRequestVouchers = requestDoc => {
    if (Array.isArray(requestDoc?.createdVoucherIds) && requestDoc.createdVoucherIds.length > 0) {
      return requestDoc.createdVoucherIds.filter(v => v && v._id);
    }
    return requestDoc?.createdVoucherId?._id ? [requestDoc.createdVoucherId] : [];
  };

  // Parked complaint answers above the entertainment limit are cleared by a
  // different right than the monthly budget, and this list carries both.
  if (
    !authCtx.checkRight('voucher:special:giftedBalanceApprove') &&
    !authCtx.checkRight('customerExperienceFeedback:special:approveHighValueVoucher')
  ) {
    return <Navigate to='/welcome' replace />;
  }

  const linkedRequest = linked.requestId
    ? requests.find(r => r._id === linked.requestId) || null
    : null;
  const linkedDecision = linkedDecisionText(linkedRequest);

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h5' fontWeight='bold'>
          Freigaben: Kulanz-/Lokalmarketing-Gutscheine
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label='Status' onChange={e => setStatus(e.target.value)}>
              <MenuItem value='pending'>offen</MenuItem>
              <MenuItem value='approved'>freigegeben</MenuItem>
              <MenuItem value='rejected'>abgelehnt</MenuItem>
              <MenuItem value='obsolete'>hinfällig</MenuItem>
              <MenuItem value=''>alle</MenuItem>
            </Select>
          </FormControl>
          <Button variant='outlined' onClick={load} disabled={isLoading}>
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {linked.requestId && !isLoading && !linkedRequest && (
        <Alert severity='info' sx={{ mb: 2 }}>
          <AlertTitle>Anfrage nicht in dieser Liste</AlertTitle>
          Die aus der E-Mail verlinkte Anfrage ist hier nicht enthalten. Sie kann zurückgezogen
          worden oder älter als die 200 zuletzt gestellten Anfragen sein.
        </Alert>
      )}

      {linkedDecision && (
        <Alert severity='info' sx={{ mb: 2 }}>
          <AlertTitle>Bereits entschieden</AlertTitle>
          {linkedDecision} Sie ist unten markiert – es ist nichts mehr zu tun.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Art</TableCell>
                <TableCell>Datum</TableCell>
                <TableCell>Einlösbar in</TableCell>
                <TableCell>Produkt / Fall</TableCell>
                <TableCell align='right'>Qty</TableCell>
                <TableCell align='right'>Wert</TableCell>
                <TableCell>Von</TableCell>
                <TableCell>Kommentar</TableCell>
                <TableCell align='right'>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Typography color='text.secondary'>Keine Einträge.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map(r => {
                  const id = r._id;
                  const isBusy = busySet.has(id);
                  const isFeedback = isFeedbackApproval(r);
                  const productName = subjectText(r);
                  const branchName =
                    r.branchId?.longName ||
                    r.relatedVoucherProductId?.branchId?.longName ||
                    r.branchId?.locationName ||
                    '-';
                  const entertainmentName =
                    r.entertainmentId?.longName ||
                    r.entertainmentId?.entertainmentName ||
                    r.feedback?.entertainmentName ||
                    '';
                  const redemptionText =
                    branchName === '-'
                      ? entertainmentName || '-'
                      : `${branchName} – ${entertainmentName || 'Alle Entertainments'}`;
                  const requestedAt = r.meta?.requestedAt
                    ? dayjs(r.meta.requestedAt).format('DD.MM.YYYY HH:mm')
                    : '-';
                  const amount = Number(r.amount || 0) || 0;
                  const qty = Number(r.quantity || 1) || 1;
                  // A parked answer is only decidable by whoever holds the right
                  // for every reason it was parked for; the backend says so.
                  const canDecide = !isFeedback || r.canDecide === true;

                  const requestComment = commentText(r);
                  const decisionComment = r.decisionComment || '';
                  const commentTitle =
                    r.status === 'rejected' && decisionComment
                      ? `Antrag: ${requestComment}\nAblehnungsgrund: ${decisionComment}`
                      : `Antrag: ${requestComment}`;

                  const isLinked = !!linked.requestId && id === linked.requestId;

                  return (
                    <TableRow
                      key={id}
                      id={`gifted-request-${id}`}
                      sx={
                        isLinked
                          ? {
                              outline: theme => `2px solid ${theme.palette.primary.main}`,
                              outlineOffset: '-2px',
                              backgroundColor: theme => theme.palette.action.selected,
                            }
                          : undefined
                      }
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {statusChip(r.status)}
                          {r.budgetExceeded && (
                            <Chip
                              size='small'
                              variant='outlined'
                              color='warning'
                              label='über Kontingent'
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          variant='outlined'
                          color={isFeedback ? 'error' : 'default'}
                          label={isFeedback ? 'Beschwerde' : 'Kulanz/Marketing'}
                        />
                      </TableCell>
                      <TableCell>{requestedAt}</TableCell>
                      <TableCell>{redemptionText}</TableCell>
                      <TableCell>{productName}</TableCell>
                      <TableCell align='right'>{qty}</TableCell>
                      <TableCell align='right'>
                        {amount.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        €
                      </TableCell>
                      <TableCell>{formatUser(r.meta?.requestedBy)}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 260,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={commentTitle}
                      >
                        {r.status === 'rejected' && decisionComment
                          ? `${requestComment} · Ablehnung: ${decisionComment}`
                          : requestComment}
                      </TableCell>
                      <TableCell align='right'>
                        {r.status === 'pending' ? (
                          <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                            {isFeedback && (
                              <IconButton
                                size='small'
                                onClick={() => setDetailRequest(r)}
                                title='Fall ansehen'
                              >
                                <VisibilityIcon fontSize='small' />
                              </IconButton>
                            )}
                            {canDecide ? (
                              <>
                                <Button
                                  size='small'
                                  variant='contained'
                                  onClick={() => handleApprove(r)}
                                  disabled={isBusy}
                                >
                                  Freigeben
                                </Button>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='error'
                                  onClick={() => openRejectDialog(r)}
                                  disabled={isBusy}
                                >
                                  Ablehnen
                                </Button>
                              </>
                            ) : (
                              <Typography variant='caption' color='text.secondary'>
                                Nicht von dir zu entscheiden
                              </Typography>
                            )}
                          </Box>
                        ) : isFeedback ? (
                          <IconButton
                            size='small'
                            onClick={() => setDetailRequest(r)}
                            title='Fall ansehen'
                          >
                            <VisibilityIcon fontSize='small' />
                          </IconButton>
                        ) : r.status === 'approved' && getRequestVouchers(r).length > 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            {getRequestVouchers(r).map(v => (
                              <Box
                                key={v._id}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <Typography variant='body2' fontFamily='monospace'>
                                  {v.voucherCode}
                                </Typography>
                                <IconButton
                                  size='small'
                                  onClick={() => handleViewVoucherPdf(v._id)}
                                  title='PDF anzeigen'
                                >
                                  <VisibilityIcon fontSize='small' />
                                </IconButton>
                                <IconButton
                                  size='small'
                                  onClick={() => handleDownloadVoucherPdf(v._id)}
                                  title='Herunterladen'
                                >
                                  <DownloadIcon fontSize='small' />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography color='text.secondary'>-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(approvedVouchers)}
        onClose={() => setApprovedVouchers(null)}
        maxWidth='sm'
        fullWidth
      >
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              {approvedVouchers?.length > 1
                ? `${approvedVouchers.length} Gutscheine erstellt`
                : 'Gutschein erstellt'}
            </Typography>
            <IconButton edge='end' color='inherit' onClick={() => setApprovedVouchers(null)}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Die Freigabe wurde erteilt und die Gutscheine wurden erstellt. Sie können hier direkt
            heruntergeladen werden und stehen auch dem Antragsteller in seinen Gutscheinen zur
            Verfügung.
          </Typography>
          {(approvedVouchers || []).map(v => (
            <Box
              key={v.voucherId || v.voucherCode}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
            >
              <Typography variant='body1' fontFamily='monospace'>
                {v.voucherCode}
              </Typography>
              <Box sx={{ display: 'inline-flex', gap: 1 }}>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleViewVoucherPdf(v.voucherId)}
                >
                  Anzeigen
                </Button>
                <Button
                  size='small'
                  variant='contained'
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadVoucherPdf(v.voucherId)}
                >
                  Herunterladen
                </Button>
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant='text' onClick={() => setApprovedVouchers(null)}>
              Schließen
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* The parked complaint answer in full: the approver decides about the
          message that goes to the customer, so it has to be readable here. */}
      <Dialog
        open={Boolean(detailRequest)}
        onClose={() => setDetailRequest(null)}
        maxWidth='md'
        fullWidth
      >
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Beschwerde-Kulanzgutschein
            </Typography>
            <IconButton edge='end' color='inherit' onClick={() => setDetailRequest(null)}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            {detailRequest
              ? `${formatEuro(detailRequest.amount)} € für ${
                  detailRequest.feedback?.customerFirstName || 'den Kunden'
                } – ${describeApprovalReasons(
                  detailRequest.reasons
                )}. Mit der Freigabe geht die Antwort unverändert raus.`
              : ''}
          </Typography>

          {detailRequest?.feedback?.message && (
            <Box>
              <Typography variant='caption' color='text.secondary'>
                Beschwerde des Kunden
              </Typography>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                {detailRequest.feedback.message}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant='caption' color='text.secondary'>
              Antwort, die nach der Freigabe gesendet wird
            </Typography>
            <Paper variant='outlined' sx={{ p: 2 }}>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                {detailRequest?.responseText || '-'}
              </Typography>
            </Paper>
          </Box>

          {detailRequest?.budgetSnapshot && (
            <Typography variant='caption' color='text.secondary'>
              Budget bei Anfrage: {formatEuro(detailRequest.budgetSnapshot.budget)} € gesamt,{' '}
              {formatEuro(detailRequest.budgetSnapshot.used)} € verbraucht,{' '}
              {formatEuro(detailRequest.budgetSnapshot.pending)} € für offene Freigaben reserviert,{' '}
              {formatEuro(detailRequest.budgetSnapshot.remaining)} € frei.
              {typeof detailRequest.entertainmentLimit === 'number' &&
                ` Limit ohne Freigabe: ${formatEuro(detailRequest.entertainmentLimit)} €.`}
            </Typography>
          )}

          {detailRequest?.decisionComment && (
            <Typography variant='caption' color='text.secondary'>
              Entscheidung: {detailRequest.decisionComment}
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant='text' onClick={() => setDetailRequest(null)}>
              Schließen
            </Button>
            {detailRequest?.status === 'pending' && detailRequest?.canDecide && (
              <>
                <Button
                  variant='outlined'
                  color='error'
                  onClick={() => openRejectDialog(detailRequest)}
                  disabled={busySet.has(detailRequest._id)}
                >
                  Ablehnen
                </Button>
                <Button
                  variant='contained'
                  onClick={() => handleApprove(detailRequest)}
                  disabled={busySet.has(detailRequest._id)}
                >
                  Freigeben &amp; senden
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth='sm' fullWidth>
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Anfrage ablehnen
            </Typography>
            <IconButton edge='end' color='inherit' onClick={closeRejectDialog}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            {isFeedbackApproval(rejectDialogRequest)
              ? 'Der Fall geht mit dieser Begründung an die Kollegin oder den Kollegen zurück; der Kunde bekommt nichts davon zu sehen.'
              : 'Ablehnungsgrund wird dem Antragsteller angezeigt.'}
          </Typography>
          <TextField
            label='Ablehnungsgrund'
            placeholder='z.B. Bitte erst Rücksprache mit Standortleitung halten'
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            autoFocus
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant='text' onClick={closeRejectDialog}>
              Abbrechen
            </Button>
            <Button variant='contained' color='error' onClick={submitReject}>
              Ablehnen
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default GiftedBalanceVoucherApprovals;

